import { Loader2 } from "lucide-react"
import type { PropsWithChildren } from "react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { FRONTEND_ROUTES, KEY_VALUES } from "@/constants"
import { PermissionsRolesEnum } from "@/enums"
import { usePermissions } from "@/hooks"
import { toast } from "@/hooks/use-toast"
import i18n from "@/i18n"
import { StorageHelper } from "@/utils"

import { resolveFrontendRoute } from "./permission-route-match"
interface PermissionContextProps {
  isLoading: boolean
  hasPermission: (
    permission: PermissionsRolesEnum[]
  ) => boolean
  // Staff Pay2B (email @pay2b.io) — exclusivo do menu Administração.
  isSuperAdmin: boolean
  // Admin do merchant (hierarquia: superAdmin ⊃ admin).
  isAdmin: boolean
  permissionGroups?: PermissionsRolesEnum[]
}
export const PermissionContext = createContext(
  {} as PermissionContextProps
)
export const PermissionContextProvider = ({
  children,
}: PropsWithChildren) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data, isFetching } = usePermissions()
  const [isCheckingPermission, setIsCheckingPermission] =
    useState(true)
  const checkedPathnameRef = useRef<string | null>(null)

  const notifyAndRedirectUnauthorized = useCallback(() => {
    // Limpa o token para evitar que LoginPage redirecione de volta
    StorageHelper.removeItem(KEY_VALUES.X_ACCESS_TOKEN_KEY)
    StorageHelper.removeItem(
      KEY_VALUES.AUTH_LOCAL_STORAGE_KEY
    )
    toast({
      title: i18n.t("auth.noPermission"),
      variant: "destructive",
    })
    navigate(FRONTEND_ROUTES.LOGIN.href, { replace: true })
  }, [navigate])

  // Casa a rota MAIS ESPECÍFICA, entende `:param` e prefere o match literal
  // — regras e porquês em ./permission-route-match.
  const getCurrentRoute = useCallback(
    () => resolveFrontendRoute(pathname)?.[1],
    [pathname]
  )

  const hasPermission = (
    permission: PermissionsRolesEnum[]
  ) => {
    if (!data?.permissionGroups) return false
    if (data.superAdmin) return true
    if (
      data.permissionGroups.includes(
        PermissionsRolesEnum.ADMIN
      )
    ) {
      return true
    }
    return permission.some((role) =>
      data.permissionGroups.includes(role)
    )
  }

  const checkAccess = useCallback(() => {
    if (pathname === FRONTEND_ROUTES.LOGIN.href) return true
    if (pathname === FRONTEND_ROUTES.FORGOT_PASSWORD.href)
      return true
    if (pathname === FRONTEND_ROUTES.FIRST_LOGIN.href)
      return true
    if (pathname === FRONTEND_ROUTES.RECOVERY_PASSWORD.href)
      return true
    if (!data?.permissionGroups) return false
    const currentRoute = getCurrentRoute()
    if (!currentRoute) return false
    if (currentRoute.permissionRoles.length === 0)
      return true
    // Exclusivo staff Pay2B (@pay2b.io).
    if (currentRoute.requireSuperAdmin) {
      return data.superAdmin
    }
    // Admin do merchant — staff Pay2B enxerga tudo
    // (hierarquia: superAdmin ⊃ admin).
    if (currentRoute.requireAdmin) {
      return data.admin || data.superAdmin
    }
    if (
      data.permissionGroups.includes(
        PermissionsRolesEnum.ADMIN
      )
    ) {
      return true
    }
    const hasPermission = currentRoute.permissionRoles.some(
      (role) => data.permissionGroups.includes(role)
    )
    return hasPermission
  }, [pathname, data, getCurrentRoute])

  useEffect(() => {
    if (isFetching) return
    if (checkedPathnameRef.current === pathname) return

    // Se não tem data.permissionGroups e não está em rota pública, redireciona
    if (!data?.permissionGroups) {
      const publicRoutes: string[] = [
        FRONTEND_ROUTES.LOGIN.href,
        FRONTEND_ROUTES.FORGOT_PASSWORD.href,
        FRONTEND_ROUTES.FIRST_LOGIN.href,
        FRONTEND_ROUTES.RECOVERY_PASSWORD.href,
      ]
      const isPublicRoute = publicRoutes.includes(pathname)

      if (!isPublicRoute) {
        checkedPathnameRef.current = pathname
        notifyAndRedirectUnauthorized()
        return
      }
    }

    const hasAccess = checkAccess()
    checkedPathnameRef.current = pathname
    if (!hasAccess) notifyAndRedirectUnauthorized()
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsCheckingPermission(false)
  }, [
    checkAccess,
    isFetching,
    notifyAndRedirectUnauthorized,
    pathname,
    data,
  ])

  const contextValue: PermissionContextProps = {
    hasPermission,
    isLoading: isCheckingPermission || isFetching,
    isSuperAdmin: data?.superAdmin || false,
    isAdmin: data?.admin || false,
    permissionGroups: data?.permissionGroups,
  }

  // Bloqueia renderização até validar permissões
  const publicRoutes: string[] = [
    FRONTEND_ROUTES.LOGIN.href,
    FRONTEND_ROUTES.FORGOT_PASSWORD.href,
    FRONTEND_ROUTES.FIRST_LOGIN.href,
    FRONTEND_ROUTES.RECOVERY_PASSWORD.href,
  ]
  const isPublicRoute = publicRoutes.includes(pathname)

  const shouldShowLoader =
    isFetching || (!isPublicRoute && isCheckingPermission)

  if (shouldShowLoader) {
    return (
      <div className="fixed inset-0 flex items-center justify-center text-center">
        <Loader2 size={32} className="animate-spin" />
      </div>
    )
  }
  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  )
}
export const usePermission = () => {
  const context = useContext(PermissionContext)
  if (!context) {
    throw new Error(
      "usePermission must be used within a PermissionProvider"
    )
  }
  return context
}
